// GOOP ZONE — Menu SFX (Procedural Web Audio API)
// ─────────────────────────────────────────────────────────────────────────────
// Lightweight procedural sound effects for menu interactions.
// No external audio files — everything is synthesized at runtime.
// ─────────────────────────────────────────────────────────────────────────────

let audioCtx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let sfxVolume = 0.6;

/** Lazily create / resume the AudioContext (must be called after user gesture) */
function getCtx(): AudioContext {
  if (!audioCtx) {
    audioCtx = new AudioContext();
    masterGain = audioCtx.createGain();
    masterGain.gain.value = sfxVolume;
    masterGain.connect(audioCtx.destination);
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
  return audioCtx;
}

function getMaster(): GainNode {
  getCtx();
  return masterGain!;
}

/** Set the SFX volume (0–1) */
export function setMenuSfxVolume(vol: number): void {
  sfxVolume = Math.max(0, Math.min(1, vol));
  if (masterGain) masterGain.gain.value = sfxVolume;
}

// ─── Click Sound ─────────────────────────────────────────────────────────────
// A short, snappy click — like tapping a mechanical switch.
// Two quick oscillator bursts with a noise transient.
export function playClick(): void {
  const ctx = getCtx();
  const master = getMaster();
  const now = ctx.currentTime;

  // Short noise burst for the "click" transient
  const bufferSize = Math.floor(ctx.sampleRate * 0.015); // 15ms
  const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = noiseBuffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
  }
  const noise = ctx.createBufferSource();
  noise.buffer = noiseBuffer;

  const noiseFilter = ctx.createBiquadFilter();
  noiseFilter.type = 'highpass';
  noiseFilter.frequency.value = 2000;

  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0.3, now);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.015);

  noise.connect(noiseFilter).connect(noiseGain).connect(master);
  noise.start(now);
  noise.stop(now + 0.02);

  // Tonal "tick" — quick sine blip
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(1200, now);
  osc.frequency.exponentialRampToValueAtTime(800, now + 0.04);

  const oscGain = ctx.createGain();
  oscGain.gain.setValueAtTime(0.2, now);
  oscGain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);

  osc.connect(oscGain).connect(master);
  osc.start(now);
  osc.stop(now + 0.06);
}

// ─── Hover Sound ─────────────────────────────────────────────────────────────
// BETA 19.34 — Dylan: "give me a solid sound effect for when i hover over
// an item. right now it sounds bad. should be a satisfying click or close
// to a click". Was a 30ms pure-sine blip — thin and unsatisfying. Now: a
// short noise transient + tonal tick (same structure as playClick but
// lower-energy + a touch softer) so the hover feels like a tactile mouse-
// over click. Distinguishable from playClick: lower noise volume, no
// tonal sustain, ends in ~30ms vs playClick's ~60ms.
export function playHover(): void {
  const ctx = getCtx();
  const master = getMaster();
  const now = ctx.currentTime;

  // Short noise burst — the "tactile" transient
  const bufferSize = Math.floor(ctx.sampleRate * 0.008); // 8ms
  const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = noiseBuffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
  }
  const noise = ctx.createBufferSource();
  noise.buffer = noiseBuffer;

  const noiseFilter = ctx.createBiquadFilter();
  noiseFilter.type = 'highpass';
  noiseFilter.frequency.value = 3000; // tighter / brighter than click's 2000

  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0.15, now); // half of playClick's 0.3
  noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.01);

  noise.connect(noiseFilter).connect(noiseGain).connect(master);
  noise.start(now);
  noise.stop(now + 0.015);

  // Tonal pluck — softer than click's
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(1600, now);
  osc.frequency.exponentialRampToValueAtTime(1100, now + 0.02);

  const oscGain = ctx.createGain();
  oscGain.gain.setValueAtTime(0.08, now);
  oscGain.gain.exponentialRampToValueAtTime(0.001, now + 0.025);

  osc.connect(oscGain).connect(master);
  osc.start(now);
  osc.stop(now + 0.03);
}

// ─── Countdown Beep ──────────────────────────────────────────────────────────
// A clear beep for each countdown number (3, 2, 1).
// Higher pitch and longer for the final "DEPLOY!" moment.
export function playCountdownBeep(num: number): void {
  const ctx = getCtx();
  const master = getMaster();
  const now = ctx.currentTime;

  const isDeploy = num <= 0;
  const freq = isDeploy ? 880 : 440 + (3 - num) * 80; // pitch rises as countdown progresses
  const duration = isDeploy ? 0.35 : 0.15;
  const volume = isDeploy ? 0.4 : 0.25;

  // Main tone
  const osc = ctx.createOscillator();
  osc.type = isDeploy ? 'sawtooth' : 'square';
  osc.frequency.setValueAtTime(freq, now);
  if (isDeploy) {
    osc.frequency.exponentialRampToValueAtTime(freq * 1.5, now + duration);
  }

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(volume, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

  osc.connect(gain).connect(master);
  osc.start(now);
  osc.stop(now + duration + 0.01);

  // Deploy gets an extra harmonic layer
  if (isDeploy) {
    const osc2 = ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(freq * 2, now);
    osc2.frequency.exponentialRampToValueAtTime(freq * 3, now + duration);

    const gain2 = ctx.createGain();
    gain2.gain.setValueAtTime(volume * 0.3, now);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + duration);

    osc2.connect(gain2).connect(master);
    osc2.start(now);
    osc2.stop(now + duration + 0.01);
  }
}

// ─── Cancel / Back Sound ─────────────────────────────────────────────────────
// A descending tone for cancelling the countdown or going back.
export function playCancel(): void {
  const ctx = getCtx();
  const master = getMaster();
  const now = ctx.currentTime;

  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(600, now);
  osc.frequency.exponentialRampToValueAtTime(300, now + 0.12);

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.2, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);

  osc.connect(gain).connect(master);
  osc.start(now);
  osc.stop(now + 0.16);
}

/** Resume audio context — call on first user interaction */
export function initMenuAudio(): void {
  getCtx();
}
